import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { FileText, X } from 'lucide-react';
import { policyAcknowledgmentService } from '@/services/policyAcknowledgmentService';

interface PolicyDocumentViewerProps {
  policyType: 'privacy' | 'tos';
  open: boolean;
  onClose: () => void;
}

export const PolicyDocumentViewer = ({ policyType, open, onClose }: PolicyDocumentViewerProps) => {
  const [policyData, setPolicyData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      loadPolicy();
    }
  }, [open, policyType]);

  const loadPolicy = async () => {
    setLoading(true);
    const type = policyType === 'privacy' ? 'privacy_policy' : 'terms_of_service';
    const { data } = await policyAcknowledgmentService.getPolicyContent(type);
    setPolicyData(data);
    setLoading(false);
  };

  const title = policyType === 'privacy' ? 'Privacy Policy' : 'Terms of Service';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[600px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-muted-foreground">Loading policy...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {policyData && (
                <>
                  <div className="flex justify-between items-center pb-2 border-b border-border">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Version: {policyData.version}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Effective: {new Date(policyData.effective_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <div 
                      className="text-foreground whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: policyData.content }}
                    />
                  </div>
                </>
              )}

              {!policyData && !loading && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Policy content not available</p>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-end pt-4 border-t border-border">
          <Button onClick={onClose} variant="outline">
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
